import { useCallback } from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import type { GridRowId } from '@mui/x-data-grid';
import type { GridInitialState } from '@mui/x-data-grid';
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';

import { ensureDefined, invariant } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import type {
  Id,
  ThirdPartyTypeEnum,
  GenericStatusEnum,
} from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { NoData } from '../Illustrations/NoData';
import { Loading } from '../Loading';
import { DataGrid } from '../DataGrid';

import { getOrganizationColumns } from './columns/get-organization-columns';
import { getPersonColumns } from './columns/get-person-columns';
import { getThirdPartyColumns } from './columns/get-third-party-columns';
import { ThirdPartiesCmp_Person_Fragment } from './fragments/person.fragment';
import { ThirdPartiesCmp_Organization_Fragment } from './fragments/organization.fragment';
import { ThirdPartiesCmp_ThirdParty_Fragment } from './fragments/third-party.fragment';
import { UseCase } from './enums/use-case.enum';
import { useThirdPartiesQuery } from './hooks/useThirdPartiesQuery.hook';
import { isSelectableIn } from './utils/is-selectable-in.util';
import { sortedByThirdPartyName } from './utils/by-third-party-name.util';
import type { SelectionRestriction } from './types/selection-restriction.type';
import type { ThirdPartiesCmp_ThirdParty } from './fragments/third-party.fragment';
import type { ThirdPartyNames } from './utils/by-third-party-name.util';

export { UseCase as ThirdPartiesUseCase };

const CENTERED_CONTENT_SX = {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
} as const;

interface Props {
  readonly excludedIds?: readonly Id[];
  readonly initialGridState?: GridInitialState;
  readonly isInternal?: boolean;
  readonly kind?: ThirdPartyTypeEnum;
  readonly selection?: readonly GridRowId[];
  readonly selectionRestriction?: SelectionRestriction;
  readonly setSelection?: (values: readonly GridRowId[]) => void;
  readonly status?: GenericStatusEnum;
  readonly subsetIds?: readonly Id[];
  readonly useCase?: UseCase;
}

const UNSELECTABLE_ROW_CLASSNAME = 'third-party-unselectable';

// The name of a third party that may not be chosen tells why.
const withUnselectableReason = <R extends Readonly<{ _id: Id }>>(
  columns: readonly GridColDef<R>[],
  restriction: SelectionRestriction | undefined,
  nameField: string,
): GridColDef<R>[] =>
  columns.map((column) =>
    restriction && column.field === nameField
      ? {
          ...column,
          renderCell: (params: GridRenderCellParams<R>) =>
            isSelectableIn(restriction, params.row._id) ? (
              String(params.formattedValue ?? EMPTY_STRING)
            ) : (
              <Tooltip title={restriction.reason}>
                <span>{String(params.formattedValue ?? EMPTY_STRING)}</span>
              </Tooltip>
            ),
        }
      : column,
  );

// Shrinks to the height it is given (page or form step) so the data grid scrolls its
// rows instead of growing with them.
const ROOT_SX = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minHeight: 0,
  width: '100%',
  pt: 2,
};

export const ThirdParties = ({
  excludedIds,
  initialGridState,
  isInternal = false,
  kind,
  selection,
  selectionRestriction,
  setSelection,
  status,
  subsetIds,
  useCase = UseCase.Visualize,
}: Readonly<Props>) => {
  const { civilities, genericStatuses } = useReferencesContext();

  const { data, isLoading } = useThirdPartiesQuery({
    isInternal,
    kind,
    status,
  });

  const handleRowSelectionModelChange = useCallback(
    (selection: Readonly<GridRowSelectionModel>) => {
      setSelection?.([...selection.ids]);
    },
    [setSelection],
  );

  const isRowSelectable = useCallback(
    ({ id }: Readonly<{ id: GridRowId }>) =>
      isSelectableIn(selectionRestriction, id),
    [selectionRestriction],
  );

  const getRowClassName = useCallback(
    ({ id }: Readonly<{ id: GridRowId }>) =>
      isSelectableIn(selectionRestriction, id)
        ? EMPTY_STRING
        : UNSELECTABLE_ROW_CLASSNAME,
    [selectionRestriction],
  );

  invariant(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    !!(useCase !== UseCase.Select || setSelection || selection) &&
      (useCase !== UseCase.Visualize || !setSelection || !selection),
    'Wrong props',
  );

  if (isLoading) {
    return <Loading />;
  }

  const buildOrganizationGrid = () => {
    invariant(
      !!data && 'findSomeOrganizations' in data,
      'findSomeOrganizations is not in data',
    );

    const organizations = getFragmentData(
      ThirdPartiesCmp_Organization_Fragment,
      data.findSomeOrganizations,
    );

    const filteredOrganizations =
      !excludedIds?.length && !subsetIds
        ? organizations
        : organizations.filter(
            (organization) =>
              !excludedIds?.includes(organization._id) &&
              (!subsetIds || subsetIds.includes(organization._id)),
          );

    if (!filteredOrganizations.length) {
      return (
        <Box sx={CENTERED_CONTENT_SX}>
          <NoData />
        </Box>
      );
    }

    return (
      <DataGrid
        columns={withUnselectableReason(
          getOrganizationColumns(useCase, genericStatuses),
          selectionRestriction,
          'name',
        )}
        getRowClassName={getRowClassName}
        initialState={initialGridState}
        isRowSelectable={isRowSelectable}
        loading={isLoading}
        rows={sortedByThirdPartyName(filteredOrganizations)}
        sx={{
          [`& .${UNSELECTABLE_ROW_CLASSNAME}`]: { color: 'text.disabled' },
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(useCase === UseCase.Select
          ? {
              checkboxSelection: true,
              onRowSelectionModelChange: handleRowSelectionModelChange,
              rowSelectionModel: { type: 'include', ids: new Set(selection) },
            }
          : EMPTY_OBJECT)}
      />
    );
  };

  const buildPersonGrid = () => {
    invariant(
      !!data && 'findSomePeople' in data,
      'findSomePeople is not in data',
    );

    const people = getFragmentData(
      ThirdPartiesCmp_Person_Fragment,
      data.findSomePeople,
    );

    const filteredPeople =
      !excludedIds?.length && !subsetIds
        ? people
        : people.filter(
            (person) =>
              !excludedIds?.includes(person._id) &&
              (!subsetIds || subsetIds.includes(person._id)),
          );

    if (!filteredPeople.length) {
      return (
        <Box sx={CENTERED_CONTENT_SX}>
          <NoData />
        </Box>
      );
    }

    return (
      <DataGrid
        columns={getPersonColumns(useCase, civilities, genericStatuses)}
        initialState={initialGridState}
        loading={isLoading}
        rows={sortedByThirdPartyName(filteredPeople)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(useCase === UseCase.Select
          ? {
              checkboxSelection: true,
              onRowSelectionModelChange: handleRowSelectionModelChange,
              rowSelectionModel: { type: 'include', ids: new Set(selection) },
            }
          : EMPTY_OBJECT)}
      />
    );
  };

  const buildThirdPartyGrid = () => {
    invariant(
      !!data && 'findSomeThirdParties' in data,
      'findSomeThirdParties is not in data',
    );

    const thirdParties = getFragmentData(
      ThirdPartiesCmp_ThirdParty_Fragment,
      data.findSomeThirdParties,
    );

    const filteredThirdParties =
      !excludedIds?.length && !subsetIds
        ? thirdParties
        : thirdParties.filter(
            (thirdParty) =>
              !excludedIds?.includes(thirdParty._id) &&
              (!subsetIds || subsetIds.includes(thirdParty._id)),
          );

    if (!filteredThirdParties.length) {
      return (
        <Box sx={CENTERED_CONTENT_SX}>
          <NoData />
        </Box>
      );
    }

    return (
      <DataGrid
        columns={getThirdPartyColumns(useCase, civilities, genericStatuses)}
        initialState={initialGridState}
        loading={isLoading}
        rows={sortedByThirdPartyName(
          filteredThirdParties as readonly (ThirdPartiesCmp_ThirdParty &
            ThirdPartyNames)[],
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(useCase === UseCase.Select
          ? {
              checkboxSelection: true,
              onRowSelectionModelChange: handleRowSelectionModelChange,
              rowSelectionModel: { type: 'include', ids: new Set(selection) },
            }
          : EMPTY_OBJECT)}
      />
    );
  };

  const { grid } = ensureDefined(
    {
      grid:
        data && 'findSomeOrganizations' in data
          ? buildOrganizationGrid()
          : data && 'findSomePeople' in data
            ? buildPersonGrid()
            : data && 'findSomeThirdParties' in data
              ? buildThirdPartyGrid()
              : null,
    },
    'No grid to display',
  );

  return <Box sx={ROOT_SX}>{grid}</Box>;
};
